import {HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {AnyAdapter} from '@drl/core/api/adapters/any-adapter.service';
import {ApiService} from '@drl/core/api/api.service';
import {CurrentUserAdapter} from '@drl/core/auth/adapters/current-user-adapter.service';
import {User} from '@drl/core/auth/models/user.model';
import {Count} from '@drl/core/models';
import {GetUsersCommand} from '@drl/shared/commands';
import {keyBy} from 'lodash-es';
import {forkJoin, Observable} from 'rxjs';
import {concatMap, map} from 'rxjs/operators';

import {PrescriptionAdapter} from './shared/adapters/prescription-adapter.service';
import {PrescriptionAuditLogsAdapter} from './shared/adapters/prescription-audit-logs-adapter.service';
import {PrescriptionMedsAdapter} from './shared/adapters/prescription-meds-adapter.service';
import {
  AddMedicineCommand,
  DelMedCommand,
  GetPatientLatestPrescriptionMedicine,
  GetPatientPrescriptionsCommand,
  GetPrescriptionAuditLogsCommand,
  PatchPrescriptionCommand,
  UpdatePrescriptionByIdCommand,
} from './shared/commands';
import {UpdateMedsCommand} from './shared/commands/update-meds.command';
import {PrescriptionMedicines, Prescriptions} from './shared/models';
import {PrescriptionAuditLog} from './shared/models/prescription-audit-log.model';
import {DeletePrescriptionFileCommand} from './prescription-list/commands/delete-presc-file.command';
import {PrescriptionStatus} from '@drl/shared/utility/patients-caregiver-utility.enum';
import {stat} from 'fs';

@Injectable({
  providedIn: 'root',
})
export class PatientEhrDataFacadeService {
  constructor(
    private readonly apiService: ApiService,
    private readonly anyAdapter: AnyAdapter,
    private readonly prescriptionAuditLogsAdapter: PrescriptionAuditLogsAdapter,
    private readonly prescriptionAdapter: PrescriptionAdapter,
    private readonly presMedAdapter: PrescriptionMedsAdapter,
    private readonly userAdapter: CurrentUserAdapter,
  ) {}

  getPatientPrecriptions(id: number, status?: string) {
    const command: GetPatientPrescriptionsCommand<Prescriptions> =
      new GetPatientPrescriptionsCommand(
        this.apiService,
        this.prescriptionAdapter,
        id,
      );
    const order = ['createdOn DESC'];
    let queryData = {
      patientId: id,
      status: undefined,
    };
    if (status) {
      queryData = {
        ...queryData,
        status: status,
      };
    }

    const queryStr = JSON.stringify({
      order,
    });

    //setting timezone
    let tz = 'America/Bahia'; //default if systme is unable to return the timezone
    if (Intl.DateTimeFormat().resolvedOptions().timeZone != 'Etc/Unknown') {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    }

    command.parameters = {
      query: new HttpParams().set('filter', queryStr).set('tz', tz),
    };
    return command.execute();
  }

  switchPresStatus(id: number, userId: number, status: boolean) {
    const command: PatchPrescriptionCommand<Partial<Prescriptions>> =
      new PatchPrescriptionCommand(
        this.apiService,
        this.prescriptionAdapter,
        id,
        userId,
      );

    command.parameters = {
      data: {
        isActive: status,
      },
    };

    return command.execute();
  }

  addMedicine(
    medicine: PrescriptionMedicines,
    presId: number,
  ): Observable<PrescriptionMedicines> {
    const command: AddMedicineCommand<PrescriptionMedicines> =
      new AddMedicineCommand(this.apiService, this.presMedAdapter, presId);
    command.parameters = {
      data: medicine,
    };
    return command.execute();
  }

  getPrescription(id: number, presId: number): Observable<Prescriptions[]> {
    const command: GetPatientPrescriptionsCommand<Prescriptions> =
      new GetPatientPrescriptionsCommand(
        this.apiService,
        this.prescriptionAdapter,
        id,
      );

    const queryData = {
      id: presId,
    };

    const queryStr = JSON.stringify({
      where: queryData,
      limit: 1,
    });

    command.parameters = {
      query: new HttpParams().set('filter', queryStr),
    };

    return command.execute();
  }

  deleteMed(id: number, presId: number): Observable<Count> {
    const command: DelMedCommand<Count> = new DelMedCommand(
      this.apiService,
      this.anyAdapter,
      presId,
    );

    const queryData = {
      id,
    };

    command.parameters = {
      query: new HttpParams().set('where', JSON.stringify(queryData)),
    };

    return command.execute();
  }

  updateSchedule(
    medicines: PrescriptionMedicines[],
    presId: number,
  ): Observable<PrescriptionMedicines[]> {
    const observables: Observable<PrescriptionMedicines>[] = medicines.map(
      medicine => {
        const command: UpdateMedsCommand<PrescriptionMedicines> =
          new UpdateMedsCommand(this.apiService, this.presMedAdapter, presId);

        const queryData = {
          id: undefined,
        };
        if (medicine.id) {
          queryData.id = medicine.id;
        }

        const medsDataToUpdate: Partial<PrescriptionMedicines> = {
          frequency: medicine.frequency,
          timeToTake: medicine.timeToTake,
          time: medicine.time,
          duration: medicine.duration,
          locationOfAdministration: medicine.locationOfAdministration,
          foodRestriction: medicine.foodRestriction,
        };

        command.parameters = {
          data: medsDataToUpdate as PrescriptionMedicines,
          query: new HttpParams().set('where', JSON.stringify(queryData)),
        };

        return command.execute();
      },
    );

    return forkJoin(observables);
  }

  updatePres(presId, userId: number, data: Partial<Prescriptions>) {
    const command: PatchPrescriptionCommand<Partial<Prescriptions>> =
      new PatchPrescriptionCommand(
        this.apiService,
        this.prescriptionAdapter,
        presId,
        userId,
      );

    command.parameters = {
      data,
    };

    return command.execute();
  }

  fetchPrescriptionAuditLogs(prescriptionId: number, limit = 10, offset = 0) {
    const command: GetPrescriptionAuditLogsCommand<PrescriptionAuditLog> =
      new GetPrescriptionAuditLogsCommand(
        this.apiService,
        this.prescriptionAuditLogsAdapter,
        prescriptionId,
      );

    command.parameters = {
      query: new HttpParams()
        .set('limit', limit.toString())
        .set('offset', offset.toString()),
    };

    return command.execute().pipe(
      concatMap(logs => {
        let callAgain = false;
        let lastCall = false;
        const previousLogsLength = logs.length;
        logs = logs.filter(log => !!log.showLog);
        if (previousLogsLength < limit) {
          lastCall = true;
        } else if (previousLogsLength && !logs.length) {
          callAgain = true;
        } else {
          // do nothing
        }
        const userIds = logs
          .filter(log => !!log.showLog)
          .map(log => log.userId);
        const usersCommand: GetUsersCommand<User> = new GetUsersCommand(
          this.apiService,
          this.userAdapter,
        );

        const queryData = {
          id: {
            inq: userIds,
          },
        };

        const queryStr = JSON.stringify({
          where: queryData,
        });
        usersCommand.parameters = {
          query: new HttpParams().set('filter', queryStr),
        };
        return usersCommand.execute().pipe(
          map(users => {
            const userIdMap = keyBy(users, 'id');
            logs.forEach(log => {
              log.actor = userIdMap[log.userId].fullName;
              log.actorInitials = `${userIdMap[log.userId].firstName.charAt(
                0,
              )}${
                userIdMap[log.userId].lastName
                  ? userIdMap[log.userId].lastName.charAt(0)
                  : ''
              }`;
              log.photoUrl = userIdMap[log.userId].photo;
            });
            return {logs, callAgain, lastCall};
          }),
        );
      }),
    );
  }

  fetchPrescriptionMedicineByPatientExternalIds(
    patientExternalIds: string[],
  ): Observable<{[key: string]: PrescriptionMedicines}> {
    const command: GetPatientLatestPrescriptionMedicine<{
      [key: string]: PrescriptionMedicines;
    }> = new GetPatientLatestPrescriptionMedicine(
      this.apiService,
      this.anyAdapter,
      undefined,
    );

    let params = new HttpParams();
    patientExternalIds.forEach(id => {
      params = params.append('patExternalIds', id);
    });
    command.parameters = {query: params};

    return command.execute().pipe(
      map((response: {[key: string]: PrescriptionMedicines}) => {
        // Transform the response to a key-value pair where key is patientExternalId

        return response;
      }),
    );
  }

  updatePrescriptionById<T>(
    presId: number,
    data: Partial<Prescriptions>,
  ): Observable<Prescriptions> {
    const command: UpdatePrescriptionByIdCommand<Prescriptions> =
      new UpdatePrescriptionByIdCommand(
        this.apiService,
        this.prescriptionAdapter,
        presId,
      );

    command.parameters = {
      data,
    };

    return command.execute();
  }

  deletePrescriptionFile(id: number) {
    const command: DeletePrescriptionFileCommand<any> =
      new DeletePrescriptionFileCommand(this.apiService, this.anyAdapter, id);

    return command.execute();
  }
}
